import type { ControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';

/**
 * Namecard-specific helper for the composition `originalValueOverrides` hook.
 *
 * Mirrors the quote-card helpers in `quoteCardsCompositionSettings.ts`: it feeds
 * `applyDefaultControlValuesToSource` so that, at element-creation time, the
 * speaker-bound Name/Title text controls never render the template's baked-in
 * placeholder text. The real values are applied a tick later by the async
 * `resolveAndApplySpeakerForCompositionElement` step.
 */

/** Loading-state text shown for a Name/Title field while speaker data resolves. */
export const NAMECARD_LOADING_PLACEHOLDER = 'Loading…';

/** Resolved speaker values already known when a namecard is created. */
export interface NamecardSpeakerValues {
  name?: string;
  /** The name parts, for a nametag that splits them (PLAT-12085). */
  firstName?: string;
  lastName?: string;
  title?: string;
  /** The Position (PLAT-12176). */
  role?: string;
  /** The Company line of a three-line nametag (PLAT-11998). */
  organization?: string;
}

/**
 * The text fields a namecard seeds; `headshot` is an image, never seeded.
 * Name and Title (and `roleLine`, phase 2's name for Title) are lines a
 * speaker may have typed or may derive from the facts (PLAT-12176).
 */
const SEEDED_TEXT_FIELDS = [
  'name',
  'firstName',
  'lastName',
  'title',
  'role',
  'organization',
  'roleLine',
] as const;
type SeededTextField = (typeof SEEDED_TEXT_FIELDS)[number];

function isSeededTextField(
  field: string | undefined,
): field is SeededTextField {
  return (SEEDED_TEXT_FIELDS as readonly string[]).includes(field ?? '');
}

/**
 * Compute the original-value override for a single control on a namecard so the
 * speaker-bound Name/Title text controls suppress the template's placeholder.
 *
 * `speakerControlBindings` is the SpeakerControl's `controlBindings` map
 * (`{ [textControlId]: SpeakerBindingKey }`); we match the given
 * control's `id` against it — the inverse of
 * `applySpeakerControlValueMappingToControls`, so we target the same controls
 * the async resolve step will later overwrite.
 *
 * Returns:
 *  - `NAMECARD_LOADING_PLACEHOLDER` when this is the bound Name/Title control and
 *    a real value is coming, so the async resolve replaces "Loading…" with it;
 *  - `''` when the field is bound but empty/missing, so the value is written as
 *    blank (`applyDefaultControlValuesToSource` only skips null/undefined, so ''
 *    is applied) — the template default is never shown and "Loading…" can never
 *    stick on a field the speaker lacks (the async step skips null/undefined);
 *  - `undefined` for non-bound / non-text controls, leaving normal default
 *    precedence (`customValue ?? originalValue`) untouched.
 */
export function getNamecardValueOverrides(
  control: ControlConfiguration,
  speakerValues: NamecardSpeakerValues | null | undefined,
  speakerControlBindings: Record<string, string> | undefined,
): unknown {
  if (!speakerValues || !speakerControlBindings) return undefined;
  if (control.type !== 'text' || !control.id) return undefined;

  const boundField = speakerControlBindings[control.id];
  if (!isSeededTextField(boundField)) return undefined;

  const isSet = (v: unknown) => typeof v === 'string' && v.trim().length > 0;
  const hasValue =
    boundField === 'name'
      ? isSet(speakerValues.name) ||
        isSet(speakerValues.firstName) ||
        isSet(speakerValues.lastName)
      : boundField === 'title' || boundField === 'roleLine'
        ? isSet(speakerValues.title) ||
          isSet(speakerValues.role) ||
          isSet(speakerValues.organization)
        : isSet(speakerValues[boundField]);
  return hasValue ? NAMECARD_LOADING_PLACEHOLDER : '';
}
